import { createReducer } from '@reduxjs/toolkit';
import { RxGateCurrentReceiptData, RXGatePageState } from './types';
import * as actions from './actions';
import { AppState } from '../../global/types';

const initialState: RXGatePageState = {
  recipeInfo: {
    data: null,
    loading: false,
    error: null,
  },
  recipeNumber: '',
  medicationsList: {
    data: null,
    loading: false,
    error: null,
  },
  provisionStatus: {
    data: null,
    loading: false,
    error: null,
  },
  rxGateCurrentReceipt: {
    data: null,
    lastUpdate: null,
    loading: false,
    error: null,
  },
  rxGateChosenReceipt: {
    data: null,
  },
  appplyRecipeResult: {
    loading: false,
    error: null,
    result: null,
  },
};

const reducer = createReducer(initialState, {
  [actions.getRecipeInfo.REQUEST]: (state) => {
    state.recipeInfo.loading = true;
    state.recipeInfo.error = null;
    state.recipeInfo.data = null;
  },
  [actions.getRecipeInfo.SUCCESS]: (state, action) => {
    state.recipeInfo.loading = false;
    state.recipeInfo.error = null;
    state.recipeInfo.data = action.payload;
  },
  [actions.getRecipeInfo.ERROR]: (state, action) => {
    state.recipeInfo.loading = false;
    state.recipeInfo.error = action.payload;
    state.recipeInfo.data = null;
  },
  [actions.setRecipeNumber.REQUEST]: (state, action) => {
    state.recipeNumber = action.payload;
  },
  [actions.getSuitableMedications.REQUEST]: (state) => {
    state.medicationsList.loading = true;
    state.medicationsList.error = null;
    state.medicationsList.data = null;
  },
  [actions.getSuitableMedications.SUCCESS]: (state, action) => {
    state.medicationsList.loading = false;
    state.medicationsList.error = null;
    state.medicationsList.data = action.payload;
  },
  [actions.getSuitableMedications.ERROR]: (state, action) => {
    state.medicationsList.loading = false;
    state.medicationsList.error = action.payload;
    state.medicationsList.data = null;
  },
  [actions.checkProvision.REQUEST]: (state) => {
    state.provisionStatus.loading = true;
    state.provisionStatus.error = null;
    state.provisionStatus.data = null;
  },
  [actions.checkProvision.SUCCESS]: (state, action) => {
    state.provisionStatus.loading = false;
    state.provisionStatus.error = null;
    state.provisionStatus.data = action.payload;
  },
  [actions.checkProvision.ERROR]: (state, action) => {
    state.provisionStatus.loading = false;
    state.provisionStatus.error = action.payload;
    state.provisionStatus.data = null;
  },
  [actions.getRxGateCurrentReceipt.REQUEST]: (state) => {
    state.rxGateCurrentReceipt.loading = true;
  },
  [actions.getRxGateCurrentReceipt.SUCCESS]: (state, action) => {
    state.rxGateCurrentReceipt.loading = false;
    state.rxGateCurrentReceipt.error = null;

    const chosenReceipt = action.payload.docs.find(
      (item: RxGateCurrentReceiptData) =>
        item.docId === state.rxGateChosenReceipt.data?.docId
    );

    const stopUpdate =
      !!state.appplyRecipeResult.result &&
      action.payload.docs.find(
        (item: RxGateCurrentReceiptData) =>
          item.docId === state.appplyRecipeResult.result
      )?.items?.length > 0;

    if (!stopUpdate) {
      state.rxGateCurrentReceipt.lastUpdate = new Date().toLocaleString();
      state.rxGateCurrentReceipt.data = action.payload.docs;
      state.rxGateChosenReceipt.data = chosenReceipt ?? null;
    }
  },
  [actions.getRxGateCurrentReceipt.ERROR]: (state, action) => {
    state.rxGateCurrentReceipt.loading = false;
    state.rxGateCurrentReceipt.lastUpdate = new Date().toLocaleString();
    state.rxGateCurrentReceipt.data = null;
    state.rxGateCurrentReceipt.error = action.payload;
  },
  [actions.setRxGateChosenReceipt.REQUEST]: (state, action) => {
    state.rxGateChosenReceipt.data = action.payload;
  },
  [actions.applyRecipe.REQUEST]: (state) => {
    state.appplyRecipeResult.loading = true;
    state.appplyRecipeResult.error = null;
    state.appplyRecipeResult.result = null;
  },
  [actions.applyRecipe.SUCCESS]: (state, action) => {
    state.appplyRecipeResult.loading = false;
    state.appplyRecipeResult.error = null;
    state.appplyRecipeResult.result = action.payload;
  },
  [actions.applyRecipe.ERROR]: (state, action) => {
    state.appplyRecipeResult.loading = false;
    state.appplyRecipeResult.error = action.payload;
    state.appplyRecipeResult.result = null;
  },
});

const recipeInfo = (state: AppState) => state.rxGatePageReducer.recipeInfo.data;
const recipeInfoLoading = (state: AppState) =>
  state.rxGatePageReducer.recipeInfo.loading;
const recipeInfoError = (state: AppState) =>
  state.rxGatePageReducer.recipeInfo.error;

const recipeNumber = (state: AppState) => state.rxGatePageReducer.recipeNumber;

const medicationsList = (state: AppState) =>
  state.rxGatePageReducer.medicationsList.data;
const medicationsListLoading = (state: AppState) =>
  state.rxGatePageReducer.medicationsList.loading;
const medicationsListError = (state: AppState) =>
  state.rxGatePageReducer.medicationsList.error;

const provisionStatus = (state: AppState) =>
  state.rxGatePageReducer.provisionStatus.data;
const provisionStatusLoading = (state: AppState) =>
  state.rxGatePageReducer.provisionStatus.loading;
const provisionStatusError = (state: AppState) =>
  state.rxGatePageReducer.provisionStatus.error;

const rxGateCurrentReceiptLoading = (state: AppState) =>
  state.rxGatePageReducer.rxGateCurrentReceipt.loading;
const rxGateCurrentReceipt = (state: AppState) =>
  state.rxGatePageReducer.rxGateCurrentReceipt.data;
const rxGateCurrentReceiptError = (state: AppState) =>
  state.rxGatePageReducer.rxGateCurrentReceipt.error;
const rxGateReceiptLastUpdate = (state: AppState) =>
  state.rxGatePageReducer.rxGateCurrentReceipt.lastUpdate;
const rxGateChosenReceipt = (state: AppState) =>
  state.rxGatePageReducer.rxGateChosenReceipt.data;

const applyRecipeResult = (state: AppState) =>
  state.rxGatePageReducer.appplyRecipeResult.result;
const applyRecipeLoading = (state: AppState) =>
  state.rxGatePageReducer.appplyRecipeResult.loading;
const applyRecipeError = (state: AppState) =>
  state.rxGatePageReducer.appplyRecipeResult.error;

const selectors = {
  recipeInfo,
  recipeInfoLoading,
  recipeInfoError,
  recipeNumber,
  medicationsList,
  medicationsListLoading,
  medicationsListError,
  provisionStatus,
  provisionStatusLoading,
  provisionStatusError,
  rxGateCurrentReceipt,
  rxGateCurrentReceiptError,
  rxGateCurrentReceiptLoading,
  rxGateReceiptLastUpdate,
  rxGateChosenReceipt,
  applyRecipeResult,
  applyRecipeLoading,
  applyRecipeError,
};

export { selectors };
export default reducer;
